/**
 * Order Card Component
 *
 * Displays a purchase order as a card with summary info and actions.
 * @see US-8.14 - Page Liste des Commandes
 */

import { Calendar, Package, Truck, MoreVertical } from 'lucide-react';
import { Badge, Button, Dropdown, DropdownItem, DropdownDivider } from '@/components/ui';
import { formatCentimes } from '@/lib/format';
import { useCurrency, useFormatDate } from '@/hooks';
import { PurchaseOrderStatus, PURCHASE_ORDER_STATUS_LABELS } from '@bstock/shared';
import type { PurchaseOrderListItem } from '@bstock/shared';

const ORDER_STATUS_VARIANTS: Record<PurchaseOrderStatus, 'warning' | 'success' | 'neutral' | 'primary'> = {
  [PurchaseOrderStatus.DRAFT]: 'warning',
  [PurchaseOrderStatus.SENT]: 'success',
  [PurchaseOrderStatus.PARTIALLY_RECEIVED]: 'primary',
  [PurchaseOrderStatus.RECEIVED]: 'success',
  [PurchaseOrderStatus.CANCELLED]: 'neutral',
};

interface OrderCardProps {
  order: PurchaseOrderListItem;
  onView: (order: PurchaseOrderListItem) => void;
  onEdit?: (order: PurchaseOrderListItem) => void;
  onSend?: (order: PurchaseOrderListItem) => void;
  onCancel?: (order: PurchaseOrderListItem) => void;
  onDelete?: (order: PurchaseOrderListItem) => void;
  onDuplicate?: (order: PurchaseOrderListItem) => void;
  isDuplicating?: boolean;
  showFinancials?: boolean;
}

export function OrderCard({
  order,
  onView,
  onEdit,
  onSend,
  onCancel,
  onDelete,
  onDuplicate,
  isDuplicating = false,
  showFinancials = false,
}: OrderCardProps) {
  const currency = useCurrency();
  const { formatDateShort } = useFormatDate();
  const isDraft = order.status === PurchaseOrderStatus.DRAFT;
  const isSent = order.status === PurchaseOrderStatus.SENT;
  const isCancelled = order.status === PurchaseOrderStatus.CANCELLED;
  const canCancel = isDraft || isSent; // US-8.19: DRAFT and SENT can be cancelled

  return (
    <div
      className={`bg-white rounded-lg border p-4 hover:shadow-md transition-shadow ${
        isCancelled ? 'opacity-60' : ''
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg text-neutral-900">{order.orderNumber}</h3>
            <Badge variant={ORDER_STATUS_VARIANTS[order.status]}>
              {PURCHASE_ORDER_STATUS_LABELS[order.status]}
            </Badge>
          </div>
          <p className="text-sm text-neutral-600">{order.supplier.name}</p>
        </div>
        <Dropdown
          trigger={
            <Button variant="ghost" size="sm" className="p-1">
              <MoreVertical className="w-4 h-4" />
            </Button>
          }
        >
          {isDraft && onEdit && (
            <DropdownItem onClick={() => onEdit(order)}>Modifier</DropdownItem>
          )}
          <DropdownItem onClick={() => onView(order)}>Voir</DropdownItem>
          {isDraft && onSend && (
            <DropdownItem onClick={() => onSend(order)}>Envoyer</DropdownItem>
          )}
          {onDuplicate && (
            <DropdownItem onClick={() => onDuplicate(order)} disabled={isDuplicating}>
              {isDuplicating ? 'Duplication...' : 'Dupliquer'}
            </DropdownItem>
          )}
          {(canCancel || (isDraft && onDelete)) && <DropdownDivider />}
          {canCancel && onCancel && (
            <DropdownItem onClick={() => onCancel(order)} className="text-danger-600">
              Annuler
            </DropdownItem>
          )}
          {isDraft && onDelete && (
            <DropdownItem onClick={() => onDelete(order)} className="text-danger-600">
              Supprimer
            </DropdownItem>
          )}
        </Dropdown>
      </div>

      {/* Info */}
      <div className="flex items-center gap-6 text-sm text-neutral-600 mb-4">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-neutral-400" />
          <span>{formatDateShort(order.orderDate)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Package className="w-4 h-4 text-neutral-400" />
          <span>{order.itemsCount} ligne{order.itemsCount > 1 ? 's' : ''}</span>
        </div>
        {order.expectedDate && (
          <div className="flex items-center gap-1.5">
            <Truck className="w-4 h-4 text-neutral-400" />
            <span>Livraison: {formatDateShort(order.expectedDate)}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-neutral-100">
        {showFinancials ? (
          <span className="text-lg font-semibold text-neutral-900">
            {formatCentimes(order.totalExclTax, currency)}
            <span className="text-sm font-normal text-neutral-500 ml-1">HT</span>
          </span>
        ) : (
          <span className="text-sm text-neutral-500">
            {order.itemsCount} ligne{order.itemsCount > 1 ? 's' : ''}
          </span>
        )}

        <div className="flex items-center gap-2">
          {isDraft && onEdit && (
            <Button variant="secondary" size="sm" onClick={() => onEdit(order)}>
              Modifier
            </Button>
          )}
          {isDraft && onSend && (
            <Button variant="primary" size="sm" onClick={() => onSend(order)}>
              Envoyer
            </Button>
          )}
          {!isDraft && (
            <Button variant="secondary" size="sm" onClick={() => onView(order)}>
              Voir
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
